import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

const SubscriptionManager = ({ onClose }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/subscription/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your sites will be unpublished but preserved for 30 days.')) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/subscription/cancel', {
        reason: 'user_request'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh subscription status
      await fetchSubscriptionStatus();
      
      alert('Subscription cancelled successfully. Your data will be preserved for 30 days.');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/subscription/reactivate', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh subscription status
      await fetchSubscriptionStatus();
      
      alert('Subscription reactivated successfully!');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'suspended': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'cancelled': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'archived': return <XCircle className="w-5 h-5 text-gray-600" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'suspended': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelled': return 'text-red-600 bg-red-50 border-red-200';
      case 'archived': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4">Loading subscription status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Subscription Management</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {subscriptionStatus && (
          <div className="space-y-6">
            {/* Current Status */}
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Current Status</h3>
              <div className="flex items-center gap-3 mb-4">
                {getStatusIcon(subscriptionStatus.subscription.status)}
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(subscriptionStatus.subscription.status)}`}>
                  {subscriptionStatus.subscription.status.toUpperCase()}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Current Plan</p>
                  <p className="font-semibold">{subscriptionStatus.subscription.plan.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Monthly Price</p>
                  <p className="font-semibold">${subscriptionStatus.subscription.plan.pricing.monthly}/month</p>
                </div>
                {subscriptionStatus.subscription.endDate && (
                  <div>
                    <p className="text-sm text-gray-600">
                      {subscriptionStatus.subscription.daysRemaining > 0 ? 'Expires' : 'Expired'}
                    </p>
                    <p className="font-semibold">{formatDate(subscriptionStatus.subscription.endDate)}</p>
                    {subscriptionStatus.subscription.daysRemaining !== null && (
                      <p className="text-sm text-gray-500">
                        {subscriptionStatus.subscription.daysRemaining > 0 
                          ? `${subscriptionStatus.subscription.daysRemaining} days remaining`
                          : `Expired ${Math.abs(subscriptionStatus.subscription.daysRemaining)} days ago`
                        }
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Auto Renew</p>
                  <p className="font-semibold">
                    {subscriptionStatus.subscription.autoRenew ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>

            {/* Next Action Warning */}
            {subscriptionStatus.subscription.nextAction && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-semibold text-yellow-800">Upcoming Action</h4>
                </div>
                <p className="text-yellow-700">
                  Your account will be {subscriptionStatus.subscription.nextAction.type} in{' '}
                  <strong>{subscriptionStatus.subscription.nextAction.daysRemaining} days</strong>{' '}
                  ({formatDate(subscriptionStatus.subscription.nextAction.date)})
                </p>
              </div>
            )}

            {/* Cancellation Reason */}
            {subscriptionStatus.subscription.cancellationReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Cancellation Reason</h4>
                <p className="text-red-700">{subscriptionStatus.subscription.cancellationReason}</p>
              </div>
            )}

            {/* Sites Overview */}
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Sites Overview</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{subscriptionStatus.sites.total}</p>
                  <p className="text-sm text-gray-600">Total Sites</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{subscriptionStatus.sites.published}</p>
                  <p className="text-sm text-gray-600">Published</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">{subscriptionStatus.sites.unpublished}</p>
                  <p className="text-sm text-gray-600">Unpublished</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-3">Actions</h3>
              <div className="flex gap-3">
                {subscriptionStatus.subscription.status === 'active' && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Cancel Subscription'}
                  </button>
                )}
                
                {['suspended', 'cancelled'].includes(subscriptionStatus.subscription.status) && (
                  <button
                    onClick={handleReactivateSubscription}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Reactivate Subscription'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManager;